/**
 * Página A4 con las Condiciones Generales del Servicio Técnico.
 *
 * Se inserta como página final tanto en `OrdenPDF.jsx` como en
 * `InformePericialPDF.jsx` para que TODO documento impreso lleve
 * adjunto el clausulado vigente que el cliente acepta al recoger.
 *
 * Diseño: texto compacto y legal en 1 página A4. Sin firma manuscrita
 * — la aceptación del cliente queda registrada digitalmente al marcar
 * las casillas en el portal público de Seguimiento.
 */
import { useEffect, useState } from 'react';
import { empresaAPI } from '@/lib/api';
import { CONDICIONES_TITULO, CONDICIONES_GENERALES } from '@/lib/condiciones';

export default function CondicionesGeneralesPDF({ orden, empresa: empresaProp, numeroOrden }) {
  const [empresaState, setEmpresaState] = useState(empresaProp || null);
  useEffect(() => {
    if (empresaProp) { setEmpresaState(empresaProp); return; }
    empresaAPI.obtener().then(r => setEmpresaState(r.data)).catch(() => {});
  }, [empresaProp]);

  const numero = numeroOrden || orden?.numero_orden || '—';
  const empresaNombre = empresaState?.nombre || 'Revix.es';

  return (
    <div
      style={{
        width: '210mm',
        minHeight: '297mm',
        padding: '14mm 15mm 12mm',
        boxSizing: 'border-box',
        background: '#fff',
        color: '#111',
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontSize: '8.5px',
        lineHeight: 1.45,
        pageBreakBefore: 'always',
        breakBefore: 'page',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
      data-testid="condiciones-generales-pdf"
    >
      {/* CABECERA */}
      <div style={{
        borderBottom: '2px solid #111',
        paddingBottom: '3mm',
        marginBottom: '5mm',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
      }}>
        <div>
          <p style={{
            fontSize: '7.5px',
            color: '#666',
            margin: 0,
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}>
            Anexo legal
          </p>
          <h2 style={{
            fontSize: '14px',
            fontWeight: 700,
            margin: '1mm 0 0',
            letterSpacing: '-0.2px',
          }}>
            {CONDICIONES_TITULO}
          </h2>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: '7.5px', color: '#888', margin: 0 }}>
            {empresaNombre}
          </p>
          <p style={{ fontSize: '7.5px', color: '#888', margin: '0.5mm 0 0' }}>
            OT: <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>{numero}</span>
          </p>
        </div>
      </div>

      {/* TEXTO LEGAL EN DOS COLUMNAS para que entre todo en 1 página A4 */}
      <div style={{
        columnCount: 2,
        columnGap: '6mm',
        columnRule: '0.5px solid #eee',
        textAlign: 'justify',
        hyphens: 'auto',
        wordBreak: 'normal',
        fontSize: '7.8px',
        lineHeight: 1.5,
      }}>
        {CONDICIONES_GENERALES.map((c) => (
          <section
            key={c.titulo}
            style={{
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
              marginBottom: '2.5mm',
            }}
          >
            <h3 style={{
              fontSize: '8.5px',
              fontWeight: 700,
              margin: 0,
              color: '#111',
            }}>
              {c.titulo}
            </h3>
            <p style={{ margin: '0.6mm 0 0', color: '#1d1d1f' }}>
              {c.cuerpo}
            </p>
          </section>
        ))}
      </div>

      {/* Pie informativo · prueba de firma digital cuando existe */}
      <div style={{
        marginTop: '5mm',
        paddingTop: '3mm',
        borderTop: '1px solid #ddd',
        fontSize: '7px',
        color: '#666',
        lineHeight: 1.5,
        breakInside: 'avoid',
        pageBreakInside: 'avoid',
      }}>
        {orden?.firma_digital?.aceptada ? (
          <div style={{
            background: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: '3px',
            padding: '3mm 4mm',
            marginBottom: '2mm',
          }}>
            <p style={{ margin: 0, fontWeight: 700, color: '#15803D', fontSize: '8px' }}>
              ✓ Consentimiento firmado digitalmente vía portal web de seguimiento
            </p>
            <p style={{ margin: '1mm 0 0', fontSize: '7px', color: '#166534' }}>
              Fecha aceptación: <strong>{new Date(orden.firma_digital.fecha).toLocaleString('es-ES')}</strong>
              {orden.firma_digital.telefono_mask ? ` · Teléfono: ${orden.firma_digital.telefono_mask}` : ''}
              {orden.firma_digital.ip ? ` · IP: ${orden.firma_digital.ip}` : ''}
              {orden.firma_digital.version_clausulado ? ` · Versión clausulado: v${orden.firma_digital.version_clausulado}` : ''}
            </p>
            <p style={{ margin: '0.8mm 0 0', fontSize: '6.5px', color: '#15803D' }}>
              Esta aceptación digital constituye firma electrónica simple según el art. 3 del Reglamento eIDAS (UE) 910/2014.
            </p>
          </div>
        ) : (
          <p style={{ margin: 0, textAlign: 'center' }}>
            La entrega del dispositivo al servicio técnico, así como el acceso al portal de seguimiento y la
            aceptación digital de estas condiciones, constituyen la conformidad expresa del cliente con el
            presente clausulado.
          </p>
        )}
        <p style={{ margin: '1mm 0 0', color: '#888', textAlign: 'center', fontSize: '6.5px' }}>
          {empresaNombre} · Condiciones vigentes en la fecha de emisión del documento · OT {numero}
        </p>
      </div>
    </div>
  );
}
